import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	InputAdornment,
	MenuItem,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SaveIcon from "@mui/icons-material/Save";
import SearchIcon from "@mui/icons-material/Search";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { DataGrid } from "@mui/x-data-grid";
import { AuthState } from "context/AuthContext";
import { GlobalState } from "context/GlobalContext";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import {
	compactInputSx,
	primaryButtonSx,
	SectionCard,
} from "../sharedStyles";
import { canEditCommissionMaster, resolveUserRole } from "../data";

const SEARCH_FIELDS = [
	"distributor",
	"store_name",
	"admsite_code",
	"billing_percent",
	"payment_percent",
	"effective_date",
	"status",
];

const READ_ONLY_DIALOG_FIELDS = ["distributor", "admsite_code"];

const EDITABLE_DIALOG_FIELDS = [
	"billing_percent",
	"payment_percent",
	"effective_date",
];

const GRID_COLUMNS = [
	{ field: "distributor", headerName: "DISTRIBUTOR", flex: 1, minWidth: 140 },
	{ field: "store_name", headerName: "STORE NAME", flex: 1.2, minWidth: 160 },
	{ field: "admsite_code", headerName: "ADM SITE", flex: 0.9, minWidth: 110 },
	{ field: "billing_percent", headerName: "BILLING %", flex: 0.7, minWidth: 100 },
	{ field: "payment_percent", headerName: "PAYMENT %", flex: 0.7, minWidth: 100 },
	{ field: "effective_date", headerName: "EFFECTIVE DATE", flex: 0.9, minWidth: 130 },
	{
		field: "status",
		headerName: "STATUS",
		flex: 0.7,
		minWidth: 110,
		headerAlign: "center",
		align: "center",
		renderCell: (params) => {
			const status =
				String(params.value || "inactive").toLowerCase() === "active"
					? "active"
					: "inactive";
			return (
				<Box
					sx={{
						width: "100%",
						height: "100%",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<Chip
						label={status}
						size="small"
						sx={{
							height: 24,
							minWidth: 72,
							fontSize: "11px",
							fontWeight: 600,
							textTransform: "capitalize",
							color: status === "active" ? "#027A48" : "#B42318",
							bgcolor: status === "active" ? "#ECFDF3" : "#FEF3F2",
							border: `1px solid ${status === "active" ? "#ABEFC6" : "#FECDCA"}`,
						}}
					/>
				</Box>
			);
		},
	},
];

const createDraftRow = (defaults = {}) => ({
	id: `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
	distributor: "",
	store_name: "",
	admsite_code: "",
	billing_percent: "",
	payment_percent: "",
	effective_date: "",
	...defaults,
});

const getResponseRows = (data) => {
	if (Array.isArray(data?.rows)) return data.rows;
	if (Array.isArray(data?.data)) return data.data;
	if (Array.isArray(data)) return data;
	return [];
};

const getStoreKey = (store) => `${store?.admsite_code || ""}`;

const hasCommissionValue = (value) => {
	if (value === "" || value === null || value === undefined) {
		return false;
	}
	if (typeof value === "number" && Number.isNaN(value)) {
		return false;
	}
	return true;
};

const hasCommissionValues = (commission = {}) =>
	hasCommissionValue(commission.billing_percent) &&
	hasCommissionValue(commission.payment_percent) &&
	hasCommissionValue(commission.effective_date);

const getCommissionStatus = (commission = {}) => {
	if (!hasCommissionValues(commission)) {
		return "inactive";
	}
	return String(commission.status || "active").toLowerCase() === "active"
		? "active"
		: "inactive";
};

const formatPercentValue = (value) => {
	if (!hasCommissionValue(value)) {
		return "";
	}
	return value;
};

const pickCommissionRow = (currentRow, nextRow) => {
	if (!currentRow) return nextRow;
	if (nextRow.status === "active" && currentRow.status !== "active") return nextRow;
	if (currentRow.status === "active" && nextRow.status !== "active") return currentRow;
	return (nextRow.effective_date || "") > (currentRow.effective_date || "")
		? nextRow
		: currentRow;
};

function MboCommissionMaster() {
	const { setSnackBar, setSnackBarOpen } = GlobalState();
	const { user } = AuthState();
	const userRole = resolveUserRole(user);
	const canEdit = canEditCommissionMaster(user);

	const [distributors, setDistributors] = useState([]);
	const [storeCounts, setStoreCounts] = useState({});
	const [selectedDistributor, setSelectedDistributor] = useState("");
	const [storeRows, setStoreRows] = useState([]);
	const [commissionRows, setCommissionRows] = useState([]);
	const [draftRows, setDraftRows] = useState([createDraftRow()]);
	const [error, setError] = useState("");
	const [loadingStores, setLoadingStores] = useState(false);
	const [loadingCommission, setLoadingCommission] = useState(false);
	const [saving, setSaving] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [selectedFile, setSelectedFile] = useState(null);
	const [addDialogOpen, setAddDialogOpen] = useState(false);
	const [dialogError, setDialogError] = useState("");
	const [tableSearch, setTableSearch] = useState("");

	const loading = loadingStores || loadingCommission;

	const showSnack = (message, severity = "info") => {
		setSnackBarOpen(true);
		setSnackBar((prev) => ({ ...prev, message, severity }));
	};

	const fetchDistributors = useCallback(async () => {
		try {
			const res = await pythonVmInstance.get(
				"/mbo-payment-working/commission-master/distributors"
			);
			setDistributors(res.data?.distributors || []);
			setStoreCounts(res.data?.store_counts || {});
		} catch (_err) {
			setDistributors([]);
			setStoreCounts({});
		}
	}, []);

	const fetchStoreRows = useCallback(async () => {
		if (!selectedDistributor) {
			setStoreRows([]);
			return;
		}

		try {
			setLoadingStores(true);
			setError("");
			const res = await pythonVmInstance.get(
				"/mbo-payment-working/commission-master/stores",
				{ params: { distributor: selectedDistributor } }
			);
			const rows = getResponseRows(res.data).map((row, index) => ({
				...row,
				id: getStoreKey(row) || `store-${index}`,
			}));
			setStoreRows(rows);
		} catch (fetchError) {
			const message =
				fetchError?.response?.data?.message ||
				fetchError?.message ||
				"Failed to load distributor stores";
			setError(message);
			setStoreRows([]);
		} finally {
			setLoadingStores(false);
		}
	}, [selectedDistributor]);

	const fetchCommissionRows = useCallback(async () => {
		if (!selectedDistributor) {
			setCommissionRows([]);
			return;
		}

		try {
			setLoadingCommission(true);
			setError("");
			const res = await pythonVmInstance.get(
				"/mbo-payment-working/commission-master",
				{ params: { distributor: selectedDistributor } }
			);
			const rows = getResponseRows(res.data).map((row, index) => ({
				...row,
				id:
					row.id ||
					`${row.admsite_code}-${row.effective_date}-${index}`,
			}));
			setCommissionRows(rows);
		} catch (fetchError) {
			const message =
				fetchError?.response?.data?.message ||
				fetchError?.message ||
				"Failed to load commission master";
			setError(message);
			setCommissionRows([]);
		} finally {
			setLoadingCommission(false);
		}
	}, [selectedDistributor]);

	useEffect(() => {
		fetchDistributors();
	}, [fetchDistributors]);

	useEffect(() => {
		fetchStoreRows();
		fetchCommissionRows();
	}, [fetchStoreRows, fetchCommissionRows]);

	useEffect(() => {
		setDraftRows((currentRows) =>
			currentRows.map((row) => ({
				...row,
				distributor: selectedDistributor || row.distributor,
			}))
		);
		setTableSearch("");
	}, [selectedDistributor]);

	const commissionByStore = useMemo(() => {
		const map = new Map();
		commissionRows.forEach((row) => {
			const key = getStoreKey(row);
			map.set(key, pickCommissionRow(map.get(key), row));
		});
		return map;
	}, [commissionRows]);

	const displayRows = useMemo(
		() =>
			storeRows.map((store) => {
				const key = getStoreKey(store);
				const commission = commissionByStore.get(key) || {};
				return {
					id: key || store.id,
					distributor: store.distributor || selectedDistributor,
					store_name: store.store_name || store.g_short_name || "",
					admsite_code: store.admsite_code || "",
					billing_percent: formatPercentValue(commission.billing_percent),
					payment_percent: formatPercentValue(commission.payment_percent),
					effective_date: commission.effective_date ?? "",
					status: getCommissionStatus(commission),
				};
			}),
		[storeRows, commissionByStore, selectedDistributor]
	);

	const filteredRows = useMemo(() => {
		const query = tableSearch.trim().toLowerCase();
		if (!query) {
			return displayRows;
		}
		return displayRows.filter((row) =>
			SEARCH_FIELDS.some((field) =>
				String(row[field] ?? "")
					.toLowerCase()
					.includes(query)
			)
		);
	}, [displayRows, tableSearch]);

	const updateDraftRow = (rowId, updates) => {
		setDraftRows((currentRows) =>
			currentRows.map((row) => (row.id === rowId ? { ...row, ...updates } : row))
		);
	};

	const addDraftRow = () => {
		setDraftRows((currentRows) => [
			...currentRows,
			createDraftRow({ distributor: selectedDistributor }),
		]);
	};

	const removeDraftRow = (rowId) => {
		setDraftRows((currentRows) => {
			if (currentRows.length <= 1) {
				return [createDraftRow({ distributor: selectedDistributor })];
			}
			return currentRows.filter((row) => row.id !== rowId);
		});
	};

	const handleStoreSelect = (rowId, storeKey) => {
		const store = storeRows.find((item) => getStoreKey(item) === storeKey);
		if (!store) {
			return;
		}

		const commission = commissionByStore.get(storeKey) || {};
		updateDraftRow(rowId, {
			distributor: store.distributor || selectedDistributor,
			store_name: store.store_name || store.g_short_name || "",
			admsite_code: store.admsite_code || "",
			billing_percent: commission.billing_percent ?? "",
			payment_percent: commission.payment_percent ?? "",
			effective_date: commission.effective_date ?? "",
		});
	};

	const openAddCommissionDialog = () => {
		if (!canEdit) {
			showSnack("Only admin and publisher can add commission.", "warning");
			return;
		}
		setDialogError("");
		setDraftRows([createDraftRow({ distributor: selectedDistributor })]);
		setAddDialogOpen(true);
	};

	const closeAddCommissionDialog = () => {
		setAddDialogOpen(false);
		setDialogError("");
		setDraftRows([createDraftRow({ distributor: selectedDistributor })]);
	};

	const handleSave = async () => {
		if (!canEdit) {
			setDialogError("Only admin and publisher can add commission.");
			return;
		}

		const rowsToSave = draftRows
			.map(({ id, ...row }) => row)
			.filter(
				(row) =>
					row.distributor &&
					row.store_name &&
					row.admsite_code &&
					row.effective_date
			);

		if (!rowsToSave.length) {
			setDialogError(
				"Fill distributor, store name, adm site, and effective date."
			);
			return;
		}

		try {
			setSaving(true);
			setDialogError("");
			await pythonVmInstance.post("/mbo-payment-working/commission-master", {
				rows: rowsToSave,
				email: user?.email || "",
			});
			closeAddCommissionDialog();
			await fetchCommissionRows();
			showSnack("Commission saved successfully", "success");
		} catch (saveError) {
			const message =
				saveError?.response?.data?.message ||
				saveError?.message ||
				"Failed to save commission";
			setDialogError(message);
			showSnack(message, "error");
		} finally {
			setSaving(false);
		}
	};

	const handleDownload = async () => {
		try {
			const params = {};
			if (selectedDistributor) {
				params.distributor = selectedDistributor;
			}
			const res = await pythonVmInstance.get(
				"/mbo-payment-working/commission-master/download",
				{ params, responseType: "blob" }
			);
			const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
			const url = URL.createObjectURL(blob);
			const link = document.createElement("a");
			const safeName = (selectedDistributor || "sample").replace(/[^0-9a-zA-Z_-]+/g, "_");
			link.href = url;
			link.setAttribute("download", `mbo_commission_master_sample_${safeName}.csv`);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(url);
			showSnack("Sample CSV downloaded", "success");
		} catch (downloadError) {
			const message =
				downloadError?.response?.data?.message ||
				downloadError?.message ||
				"Failed to download commission CSV";
			setError(message);
			showSnack(message, "error");
		}
	};

	const handleUpload = async () => {
		if (!canEdit) {
			setError("Only admin and publisher can upload commission.");
			return;
		}

		if (!selectedFile) {
			setError("Select a CSV file to upload");
			return;
		}

		try {
			setUploading(true);
			setError("");
			const formData = new FormData();
			formData.append("file", selectedFile);
			formData.append("email", user?.email || "");
			await pythonVmInstance.post(
				"/mbo-payment-working/commission-master/upload",
				formData
			);
			setSelectedFile(null);
			await fetchCommissionRows();
			showSnack("Commission CSV uploaded successfully", "success");
		} catch (uploadError) {
			const message =
				uploadError?.response?.data?.message ||
				uploadError?.message ||
				"Failed to upload commission CSV";
			setError(message);
			showSnack(message, "error");
		} finally {
			setUploading(false);
		}
	};

	return (
		<Stack spacing={3}>
			<SectionCard title="Commission Master">
				<Stack direction="row" flexWrap="wrap" useFlexGap spacing={1.5} mb={2}>
					<TextField
						select
						label="Distributor"
						size="small"
						value={selectedDistributor}
						onChange={(event) => setSelectedDistributor(event.target.value)}
						sx={{ minWidth: 280, ...compactInputSx }}
					>
						<MenuItem value="">Select distributor</MenuItem>
						{distributors.map((distributor) => (
							<MenuItem key={distributor} value={distributor}>
								{distributor}
								{storeCounts[distributor]
									? ` (${storeCounts[distributor]} stores)`
									: ""}
							</MenuItem>
						))}
					</TextField>
					{canEdit ? (
						<Button
							variant="contained"
							size="small"
							startIcon={<AddIcon />}
							onClick={openAddCommissionDialog}
							disabled={!selectedDistributor}
							sx={primaryButtonSx}
						>
							Add Commission
						</Button>
					) : null}
					<Button
						variant="outlined"
						size="small"
						startIcon={<FileDownloadIcon />}
						onClick={handleDownload}
						disabled={!selectedDistributor}
					>
						Download Sample CSV
					</Button>
					{canEdit ? (
						<>
							<Button
								variant="outlined"
								size="small"
								component="label"
								startIcon={<UploadFileIcon />}
							>
								{selectedFile ? selectedFile.name : "Choose CSV"}
								<input
									hidden
									type="file"
									accept=".csv"
									onChange={(event) =>
										setSelectedFile(event.target.files?.[0] || null)
									}
								/>
							</Button>
							<Button
								variant="contained"
								size="small"
								startIcon={<UploadFileIcon />}
								onClick={handleUpload}
								disabled={!selectedFile || uploading}
								sx={primaryButtonSx}
							>
								{uploading ? "Uploading..." : "Upload CSV"}
							</Button>
						</>
					) : null}
				</Stack>

				{!canEdit ? (
					<Alert severity="info" sx={{ borderRadius: 2, mb: 2 }}>
						You have <strong>{userRole}</strong> access — commission rates are
						view only. Only admin and publisher can add or upload commission.
					</Alert>
				) : null}

				{!selectedDistributor ? (
					<Typography variant="body2" color="text.secondary">
						Select a distributor to view store and commission details.
					</Typography>
				) : (
					<Stack spacing={1.5}>
						<TextField
							size="small"
							placeholder="Search store, adm site, status..."
							value={tableSearch}
							onChange={(event) => setTableSearch(event.target.value)}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<SearchIcon sx={{ fontSize: 20 }} />
									</InputAdornment>
								),
							}}
							sx={{ maxWidth: 360, ...compactInputSx }}
						/>
						<Box sx={{ height: 420, width: "100%" }}>
							<DataGrid
								rows={filteredRows}
								columns={GRID_COLUMNS}
								loading={loading}
								disableRowSelectionOnClick
								pageSizeOptions={[10, 25, 50]}
								initialState={{
									pagination: { paginationModel: { pageSize: 25 } },
								}}
								sx={{
									"& .MuiDataGrid-cell": {
										display: "flex",
										alignItems: "center",
									},
								}}
							/>
						</Box>
					</Stack>
				)}
			</SectionCard>

			<Dialog
				open={addDialogOpen}
				onClose={closeAddCommissionDialog}
				fullWidth
				maxWidth="lg"
			>
				<DialogTitle>Add Commission</DialogTitle>
				<DialogContent dividers>
					<Stack spacing={2} sx={{ pt: 1 }}>
						{dialogError && (
							<Alert severity="error" sx={{ borderRadius: 2 }}>
								{dialogError}
							</Alert>
						)}
						{draftRows.map((row) => (
							<Stack
								key={row.id}
								direction="row"
								flexWrap="wrap"
								useFlexGap
								spacing={1}
							>
								<TextField
									select
									label="Store"
									size="small"
									value={getStoreKey(row)}
									onChange={(event) =>
										handleStoreSelect(row.id, event.target.value)
									}
									disabled={!selectedDistributor || !storeRows.length}
									sx={{ minWidth: 260, ...compactInputSx }}
								>
									<MenuItem value="">Select store</MenuItem>
									{storeRows.map((store) => (
										<MenuItem key={getStoreKey(store)} value={getStoreKey(store)}>
											{store.store_name || store.g_short_name}
											{store.admsite_code ? ` (${store.admsite_code})` : ""}
										</MenuItem>
									))}
								</TextField>
								{READ_ONLY_DIALOG_FIELDS.map((field) => (
									<TextField
										key={`${row.id}-${field}-readonly`}
										label={field.replace(/_/g, " ")}
										size="small"
										value={
											field === "distributor"
												? row.distributor || selectedDistributor
												: row[field]
										}
										InputProps={{ readOnly: true }}
										disabled
										sx={{ minWidth: 160, ...compactInputSx }}
									/>
								))}
								{EDITABLE_DIALOG_FIELDS.map((field) => (
									<TextField
										key={`${row.id}-${field}`}
										label={field.replace(/_/g, " ")}
										size="small"
										type={
											field.includes("percent")
												? "number"
												: field === "effective_date"
													? "date"
													: "text"
										}
										value={row[field]}
										onChange={(event) =>
											updateDraftRow(row.id, { [field]: event.target.value })
										}
										InputLabelProps={
											field === "effective_date" ? { shrink: true } : undefined
										}
										sx={{ minWidth: 140, ...compactInputSx }}
									/>
								))}
								<Button color="error" onClick={() => removeDraftRow(row.id)}>
									Remove
								</Button>
							</Stack>
						))}
						<Button
							startIcon={<AddIcon />}
							onClick={addDraftRow}
							disabled={!selectedDistributor}
						>
							Add Row
						</Button>
					</Stack>
				</DialogContent>
				<DialogActions sx={{ px: 3, py: 2 }}>
					<Button onClick={closeAddCommissionDialog}>Cancel</Button>
					<Button
						variant="contained"
						startIcon={<SaveIcon />}
						onClick={handleSave}
						disabled={saving || !selectedDistributor}
						sx={primaryButtonSx}
					>
						{saving ? "Saving..." : "Save Commission"}
					</Button>
				</DialogActions>
			</Dialog>

			{error && (
				<Alert severity="error" sx={{ borderRadius: 2 }}>
					{error}
				</Alert>
			)}
		</Stack>
	);
}

export default MboCommissionMaster;
